import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../auth/config.js';
import { getCachedAccessToken, userManager } from '../auth/userManager.js';

// API response envelope (mirrors the TIPSSSO API { success, data, error } shape).
export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach the ZITADEL access token (cached from oidc-client-ts) as a Bearer.
api.interceptors.request.use((config) => {
  const token = getCachedAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or revoked — try a silent renew once, otherwise kick to login.
      try {
        const user = await userManager.signinSilent();
        if (user?.access_token) {
          // Caller can retry; we don't auto-replay to avoid loops.
          return Promise.reject(error);
        }
      } catch {
        /* fall through to redirect */
      }
      if (!window.location.pathname.startsWith('/auth')) {
        void userManager.signinRedirect({
          state: { returnTo: window.location.pathname + window.location.search },
        });
      }
    }
    return Promise.reject(error);
  },
);

/** Normalize an axios/error into a human-readable message. */
export function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiEnvelope<unknown> | undefined;
    return data?.message || data?.error || err.message || 'Request failed';
  }
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}

// ---- Typed helpers ---------------------------------------------------------
// Server replies with { success, data }. These unwrap `data` for the caller.

async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const resp = await promise;
  const body = resp.data;
  // The TIPSSSO API returns { data } on success and { error, message } on failure
  // (failures are non-2xx, which axios already rejects). Most endpoints do NOT set a
  // `success` field, so a 2xx response is success unless `success` is explicitly false.
  if (body && body.success === false) {
    throw new Error(body.message || body.error || 'Request failed');
  }
  return body.data as T;
}

export function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.get<ApiEnvelope<T>>(url, config));
}

export function apiPost<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.post<ApiEnvelope<T>>(url, body, config));
}

export function apiPut<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.put<ApiEnvelope<T>>(url, body, config));
}

export function apiPatch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.patch<ApiEnvelope<T>>(url, body, config));
}

export function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return unwrap<T>(api.delete<ApiEnvelope<T>>(url, config));
}
